import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Mail, Users, CheckCircle, XCircle, TrendingUp, Calendar, Download } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [topCampaigns, setTopCampaigns] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, historyRes, performanceRes, campaignsRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/history?limit=20`),
        axios.get(`${API}/analytics/performance`),
        axios.get(`${API}/analytics/top-campaigns`)
      ]);
      setStats(statsRes.data);
      setHistory(historyRes.data);
      setPerformance(performanceRes.data);
      setTopCampaigns(campaignsRes.data.top_campaigns || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportDashboardCSV = () => {
    const csvData = [
      ['Metrik', 'Değer'],
      ['Toplam Şablon', stats?.total_templates || 0],
      ['Toplam Alıcı', stats?.total_recipients || 0],
      ['Gönderilen Email', stats?.total_sent || 0],
      ['Başarısız', stats?.total_failed || 0],
      ['Başarı Oranı', `${stats?.success_rate || 0}%`],
      ['Açılma Oranı', `${stats?.open_rate || 0}%`],
      ['Tıklama Oranı', `${stats?.click_rate || 0}%`],
      ['Bugün Gönderilen', stats?.today_sent || 0],
      ['Bu Hafta Gönderilen', stats?.week_sent || 0]
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dashboard_rapor_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('📊 Dashboard raporu indirildi!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6366f1]"></div>
      </div>
    );
  }

  const kpiCards = [
    { label: 'Toplam Şablon', value: stats?.total_templates || 0, icon: Mail, color: '#6366f1' },
    { label: 'Toplam Alıcı', value: stats?.total_recipients || 0, icon: Users, color: '#10b981' },
    { label: 'Gönderilen Email', value: stats?.total_sent || 0, icon: CheckCircle, color: '#6366f1' },
    { label: 'Başarısız', value: stats?.total_failed || 0, icon: XCircle, color: '#ef4444' },
  ];

  const pieData = [
    { name: 'Gönderildi', value: stats?.total_sent || 0, color: '#10b981' },
    { name: 'Başarısız', value: stats?.total_failed || 0, color: '#ef4444' },
  ];

  const weekData = [
    { day: 'Pzt', sent: 45 },
    { day: 'Sal', sent: 78 },
    { day: 'Çar', sent: 123 },
    { day: 'Per', sent: 95 },
    { day: 'Cum', sent: 156 },
    { day: 'Cmt', sent: 89 },
    { day: 'Paz', sent: 67 },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-[#0f172a] tracking-tight">Kontrol Paneli</h1>
          <p className="text-lg text-[#64748b] mt-2">Email pazarlama kampanyalarınızın özeti</p>
        </div>
        <Button onClick={exportDashboardCSV} variant="outline">
          <Download size={16} className="mr-2" />
          Rapor İndir (CSV)
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {kpiCards.map((kpi, index) => (
          <div
            key={index}
            data-testid={`kpi-card-${kpi.label.toLowerCase().replace(' ', '-')}`}
            className="bg-white rounded-xl border border-[#e2e8f0] p-6 card-hover"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 rounded-lg" style={{ backgroundColor: `${kpi.color}15` }}>
                <kpi.icon size={24} color={kpi.color} strokeWidth={1.5} />
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-[#64748b] mb-1">{kpi.label}</p>
              <p className="text-3xl font-extrabold text-[#0f172a]">{kpi.value.toLocaleString()}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Email Analytics - Resend Metrikleri */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500 rounded-lg">
              <TrendingUp size={24} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-[#0f172a]">Email Performance Metrikleri</h2>
              <p className="text-sm text-[#64748b]">Resend Dashboard'dan canlı veriler</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {/* Açılma Oranı */}
          <div className="bg-white rounded-lg p-4 border border-blue-100">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-[#64748b]">Açılma Oranı</p>
              <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                stats?.open_rate >= 20 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                Hedef: 20%+
              </span>
            </div>
            <p className="text-2xl font-extrabold text-[#0f172a] mb-1">
              {stats?.open_rate !== null && stats?.open_rate !== undefined ? `${stats.open_rate}%` : '-'}
            </p>
            <p className="text-xs text-[#94a3b8]">
              {stats?.open_rate ? 'Resend tracking aktif' : 'Henüz veri yok'}
            </p>
          </div>

          {/* Tıklama Oranı */}
          <div className="bg-white rounded-lg p-4 border border-blue-100">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-[#64748b]">Tıklama Oranı</p>
              <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                stats?.click_rate >= 2 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
                Hedef: 2%+
              </span>
            </div>
            <p className="text-2xl font-extrabold text-[#0f172a] mb-1">
              {stats?.click_rate !== null && stats?.click_rate !== undefined ? `${stats.click_rate}%` : '-'}
            </p>
            <p className="text-xs text-[#94a3b8]">
              {stats?.click_rate ? 'Resend tracking aktif' : 'Henüz veri yok'}
            </p>
          </div>

          {/* Teslimat Oranı */}
          <div className="bg-white rounded-lg p-4 border border-blue-100">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-[#64748b]">Teslimat Oranı</p>
              <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full font-bold">Hedef: 95%+</span>
            </div>
            <p className="text-2xl font-extrabold text-[#0f172a] mb-1">
              {stats?.total_sent > 0 ? Math.round((stats.total_sent / (stats.total_sent + stats.total_failed)) * 100) : '-'}%
            </p>
            <p className="text-xs text-[#94a3b8]">
              {stats?.total_sent > 0 ? `${stats.total_sent} / ${stats.total_sent + stats.total_failed}` : 'Henüz veri yok'}
            </p>
          </div>

          {/* Bounce Oranı */}
          <div className="bg-white rounded-lg p-4 border border-blue-100">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-[#64748b]">Bounce Oranı</p>
              <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                (stats?.bounce_rate || 0) <= 5 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                Max: 5%
              </span>
            </div>
            <p className="text-2xl font-extrabold text-[#0f172a] mb-1">
              {stats?.bounce_rate !== null && stats?.bounce_rate !== undefined ? `${stats.bounce_rate}%` : '0%'}
            </p>
            <p className="text-xs text-[#94a3b8]">
              {stats?.total_failed > 0 ? `${stats.total_failed} bounce` : 'Harika! Bounce yok'}
            </p>
          </div>

          {/* Spam Şikayet */}
          <div className="bg-white rounded-lg p-4 border border-blue-100">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-[#64748b]">Spam Şikayet</p>
              <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                (stats?.spam_rate || 0) <= 0.1 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}>
                Max: 0.1%
              </span>
            </div>
            <p className="text-2xl font-extrabold text-[#0f172a] mb-1">
              {stats?.spam_rate !== null && stats?.spam_rate !== undefined ? `${stats.spam_rate}%` : '-'}
            </p>
            <p className="text-xs text-[#94a3b8]">
              {stats?.spam_rate ? 'Resend tracking aktif' : 'Henüz veri yok'}
            </p>
          </div>
        </div>

        <div className="mt-4 p-4 bg-blue-100 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>💡 Not:</strong> Açılma ve tıklama oranları Resend Dashboard'da görüntülenebilir. 
            Daha detaylı analytics için{' '}
            <a 
              href="https://resend.com/emails" 
              target="_blank" 
              rel="noopener noreferrer"
              className="underline font-bold hover:text-blue-900"
            >
              Resend Dashboard
            </a>
            'u ziyaret edin.
          </p>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Weekly Trend */}
        <div className="md:col-span-2 bg-white rounded-xl border border-[#e2e8f0] p-6">
          <div className="flex items-center gap-3 mb-6">
            <TrendingUp size={24} className="text-[#6366f1]" />
            <h2 className="text-xl font-bold text-[#0f172a]">Haftalık Gönderim Trendi</h2>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={weekData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="day" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                }}
              />
              <Line type="monotone" dataKey="sent" stroke="#6366f1" strokeWidth={3} dot={{ fill: '#6366f1', r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Success Rate */}
        <div className="bg-white rounded-xl border border-[#e2e8f0] p-6">
          <h2 className="text-xl font-bold text-[#0f172a] mb-6">Başarı Oranı</h2>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 text-center">
            <p className="text-3xl font-extrabold text-[#0f172a]">{stats?.success_rate || 0}%</p>
            <p className="text-sm text-[#64748b]">Genel Başarı Oranı</p>
          </div>
        </div>
      </div>

      {/* Hourly Performance */}
      {performance && performance.hourly_performance && performance.hourly_performance.length > 0 && (
        <div className="bg-white rounded-xl border border-[#e2e8f0] p-6">
          <h2 className="text-xl font-bold text-[#0f172a] mb-6">📊 Saatlik Performans</h2>
          <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-900">
              <strong>🏆 En İyi Saat:</strong> {performance.best_hour}:00 - {performance.best_hour_opens} açılma
            </p>
            <p className="text-xs text-blue-700 mt-1">
              Email gönderme için optimize edilmiş zaman
            </p>
          </div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={performance.hourly_performance}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="hour" stroke="#64748b" label={{ value: 'Saat', position: 'insideBottom', offset: -5 }} />
              <YAxis stroke="#64748b" />
              <Tooltip />
              <Legend />
              <Bar dataKey="sent" fill="#6366f1" name="Gönderilen" />
              <Bar dataKey="opened" fill="#10b981" name="Açılan" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Top Campaigns */}
      {topCampaigns && topCampaigns.length > 0 && (
        <div className="bg-white rounded-xl border border-[#e2e8f0] p-6">
          <h2 className="text-xl font-bold text-[#0f172a] mb-6">🎯 En Başarılı Kampanyalar</h2>
          <div className="space-y-3">
            {topCampaigns.map((campaign, idx) => (
              <div key={campaign.campaign_id} className="p-4 bg-gradient-to-r from-[#f8fafc] to-white rounded-lg border border-[#e2e8f0]">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : '📧'}</span>
                    <span className="font-semibold text-[#0f172a]">{campaign.name || 'Kampanya'}</span>
                  </div>
                  <span className="text-xs text-[#64748b]">
                    {new Date(campaign.sent_at).toLocaleDateString('tr-TR')}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-[#64748b]">Alıcı</p>
                    <p className="text-lg font-bold text-[#0f172a]">{campaign.total_recipients}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748b]">Açılma</p>
                    <p className="text-lg font-bold text-[#10b981]">{campaign.open_rate}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#64748b]">Tıklama</p>
                    <p className="text-lg font-bold text-[#6366f1]">{campaign.click_rate}%</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div className="bg-white rounded-xl border border-[#e2e8f0] p-6">
        <div className="flex items-center gap-3 mb-6">
          <Calendar size={24} className="text-[#6366f1]" />
          <h2 className="text-xl font-bold text-[#0f172a]">Son Aktiviteler</h2>
        </div>
        <div className="space-y-3">
          {history.slice(0, 5).map((record) => (
            <div
              key={record.id}
              className="flex items-center justify-between p-4 bg-[#f8fafc] rounded-lg border border-[#e2e8f0]"
            >
              <div className="flex items-center gap-3">
                {record.status === 'sent' ? (
                  <CheckCircle size={20} className="text-[#10b981]" />
                ) : (
                  <XCircle size={20} className="text-[#ef4444]" />
                )}
                <div>
                  <p className="font-medium text-[#0f172a]">{record.recipient_email}</p>
                  <p className="text-sm text-[#64748b]">{record.template_name}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-[#64748b]">
                  {new Date(record.sent_at).toLocaleString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;